import { useNavigate } from 'react-router-dom';
import Drawer from '@/components/base/Drawer';
import Badge, { statusTone } from '@/components/base/Badge';
import { useAppStore } from '@/store/AppStore';
import { formatTL } from '@/lib/format';
import { APPOINTMENT_STATUSES } from '@/mocks/constants';
import type { Appointment } from '@/types';

export default function AppointmentDetail({
  appointment,
  onClose,
}: {
  appointment: Appointment;
  onClose: () => void;
}) {
  const { teams, updateAppointment } = useAppStore();
  const navigate = useNavigate();

  return (
    <Drawer open onClose={onClose} title={appointment.id} width="max-w-2xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="font-heading text-lg font-bold text-foreground-950">{appointment.customerName}</div>
            <div className="text-sm text-foreground-500">{appointment.service}</div>
          </div>
          <Badge tone={statusTone(appointment.status)}>{appointment.status}</Badge>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <a
            href={`tel:${appointment.phone.replace(/\s/g, '')}`}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-phone-line" /> Ara
          </a>
          <a
            href={`https://wa.me/90${appointment.phone.replace(/\s/g, '').slice(1)}`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-whatsapp-line" /> WhatsApp
          </a>
          <button
            type="button"
            onClick={() => navigate('/bugunun-isleri')}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            <i className="ri-play-circle-line" /> İşi Aç
          </button>
        </div>

        {/* Info */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-3">
            <div className="text-xs font-semibold text-foreground-500">Tarih / Saat</div>
            <div className="mt-1 text-sm font-semibold text-foreground-900">
              {appointment.date} · {appointment.startTime}
            </div>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-3">
            <div className="text-xs font-semibold text-foreground-500">Ekip</div>
            <div className="mt-1 text-sm font-semibold text-foreground-900">{appointment.team}</div>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-3">
            <div className="text-xs font-semibold text-foreground-500">Konum</div>
            <div className="mt-1 text-sm font-semibold text-foreground-900">{appointment.location}</div>
            <div className="text-xs text-foreground-500">{appointment.address}</div>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-3">
            <div className="text-xs font-semibold text-foreground-500">Süre</div>
            <div className="mt-1 text-sm font-semibold text-foreground-900">{appointment.duration}</div>
          </div>
        </div>

        {/* Linked quote */}
        {appointment.quoteId && (
          <div className="flex items-center justify-between rounded-lg bg-primary-950 px-4 py-3">
            <div>
              <div className="text-xs text-primary-300">Bağlı Teklif</div>
              <div className="text-sm font-semibold text-white">{appointment.quoteId}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-primary-300">Teklif Tutarı</div>
              <div className="font-heading text-lg font-bold text-accent-400">{formatTL(appointment.quoteAmount)}</div>
            </div>
          </div>
        )}

        {/* Notes */}
        {appointment.customerNote && (
          <p className="rounded-lg bg-background-50 p-3 text-sm text-foreground-600">
            <span className="font-semibold text-foreground-800">Müşteri notu: </span>
            {appointment.customerNote}
          </p>
        )}
        {appointment.internalNote && (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <span className="font-semibold">Dahili not: </span>
            {appointment.internalNote}
          </p>
        )}

        {/* Ekip değiştir */}
        <div>
          <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Ekip Değiştir</h4>
          <select
            className="h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
            value={appointment.team}
            onChange={(e) => updateAppointment(appointment.id, { team: e.target.value })}
          >
            {teams.map((t) => (
              <option key={t.id} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div>
          <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Durum</h4>
          <select
            className="h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
            value={appointment.status}
            onChange={(e) => updateAppointment(appointment.id, { status: e.target.value })}
          >
            {APPOINTMENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* Ertele / İptal quick actions */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => updateAppointment(appointment.id, { status: 'Ertelendi' })}
            className="flex-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100"
          >
            Ertele
          </button>
          <button
            type="button"
            onClick={() => updateAppointment(appointment.id, { status: 'İptal' })}
            className="flex-1 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
          >
            İptal
          </button>
        </div>
      </div>
    </Drawer>
  );
}